import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useParams, Link, useNavigate } from "@tanstack/react-router";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format, isSameDay, parseISO } from "date-fns";
import { ArrowLeft, CalendarRange, Filter, LayoutGrid, Plus, Search, Settings2, Users, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { isBoardManager, midpoint, type KCard } from "@/lib/kanban";
import { useBoardBundle, useAllProfilesLite, useActiveProfilesLite } from "@/hooks/use-kanban";
import { KanbanColumn } from "@/components/kanban/KanbanColumn";
import { UserAvatar } from "@/components/common/UserAvatar";
import { KanbanCardFace } from "@/components/kanban/KanbanCard";
import { CardDrawer } from "@/components/kanban/CardDrawer";
import { NewCardDialog } from "@/components/kanban/NewCardDialog";
import { EditCardDialog } from "@/components/kanban/EditCardDialog";
import { ManageFieldsDialog } from "@/components/kanban/ManageFieldsDialog";
import { ManageMembersDialog } from "@/components/kanban/ManageMembersDialog";
import { WeeklyStrip } from "@/components/kanban/WeeklyStrip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const sb = supabase as never as typeof supabase;

export const Route = createFileRoute("/_app/boards/$id")({ component: BoardPage });

function BoardPage() {
  const { id } = useParams({ from: "/_app/boards/$id" });
  const { user, profile, role } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: bundle, isLoading } = useBoardBundle(id);
  const { data: allProfiles = [] } = useAllProfilesLite();
  const { data: activeProfilesList = [] } = useActiveProfilesLite();
  const activeIds = useMemo(
    () => new Set(activeProfilesList.map((p) => p.id as string)),
    [activeProfilesList],
  );

  const [activeCard, setActiveCard] = useState<KCard | null>(null);
  const [drawerCard, setDrawerCard] = useState<KCard | null>(null);
  const [newCardOpen, setNewCardOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [undatedSelected, setUndatedSelected] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [weekAnchor, setWeekAnchor] = useState<Date | null>(null);
  const [prevView, setPrevView] = useState<{ day: Date | null; undated: boolean } | null>(null);
  const [localCards, setLocalCards] = useState<KCard[] | null>(null);
  const [editCard, setEditCard] = useState<KCard | null>(null);
  const [deleteCardObj, setDeleteCardObj] = useState<KCard | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Drop optimistic overrides whenever fresh server data lands.
  useEffect(() => {
    setLocalCards(null);
  }, [bundle?.cards]);

  const rawCards = localCards ?? bundle?.cards ?? [];
  const columns = bundle?.columns ?? [];
  const members = bundle?.members ?? [];
  const fieldDefs = bundle?.fieldDefs ?? [];
  const board = bundle?.board;

  const viewer = {
    role,
    userId: user?.id,
    isTeamLead: !!profile?.is_team_lead,
    department: profile?.department ?? null,
  };
  const isManager = board ? isBoardManager(viewer, board) : false;
  const isPersonal = board?.board_type === "personal";
  const isAdmin = role === "admin";
  const firstColumnId = columns[0]?.id ?? null;

  /**
   * Same source of truth as RLS (kb_can_manage_board): admin, board owner or
   * department lead may edit/delete any card on the board.
   */
  const cardActions = (_card: KCard) => ({ canEdit: isManager, canDelete: isManager });

  const handleDeleteCard = async (card: KCard) => {
    const { error } = await sb.from("kanban_cards").delete().eq("id", card.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Card deleted");
    setDeleteCardObj(null);
    setDrawerCard(null);
    qc.invalidateQueries({ queryKey: ["kanban", "board", id] });
  };

  // Plain member: only their own cards are visible on this board.
  const cards = useMemo(
    () => (isManager ? rawCards : rawCards.filter((c) => c.assignee_id === user?.id)),
    [rawCards, isManager, user?.id],
  );

  const fieldValuesByCard = useMemo(() => {
    const m = new Map<string, Record<string, string>>();
    (bundle?.fieldValues ?? []).forEach((v) => {
      if (v.value == null) return;
      const cur = m.get(v.card_id) ?? {};
      cur[v.field_id] = v.value;
      m.set(v.card_id, cur);
    });
    return m;
  }, [bundle?.fieldValues]);

  const profileMap = useMemo(() => {
    const m = new Map<string, { full_name: string; avatar_url: string | null; department: string }>();
    allProfiles.forEach((p) =>
      m.set(p.id as string, {
        full_name: p.full_name as string,
        avatar_url: (p.avatar_url as string | null) ?? null,
        department: p.department as string,
      }),
    );
    return m;
  }, [allProfiles]);

  const memberProfiles = useMemo(
    () =>
      members
        .filter((m) => activeIds.has(m.user_id))
        .map((m) => ({ id: m.user_id, full_name: profileMap.get(m.user_id)?.full_name ?? "" }))
        .filter((p) => p.full_name),
    [members, profileMap, activeIds],
  );

  const finalColumnId = columns.length ? columns[columns.length - 1].id : null;
  const wipFlagged = useMemo(() => {
    const byUser = new Map<string, number>();
    for (const c of cards) {
      if (c.column_id === finalColumnId) continue;
      byUser.set(c.assignee_id, (byUser.get(c.assignee_id) ?? 0) + 1);
    }
    const flagged = new Set<string>();
    byUser.forEach((n, uid) => n >= 3 && flagged.add(uid));
    return flagged;
  }, [cards, finalColumnId]);

  const cardsByColumn = useMemo(() => {
    const m = new Map<string, KCard[]>();
    columns.forEach((c) => m.set(c.id, []));
    const filtered = cards.filter((c) => {
      if (undatedSelected) {
        if (c.work_date) return false;
      } else if (selectedDay) {
        if (!(c.work_date && isSameDay(parseISO(c.work_date), selectedDay))) return false;
      }
      return true;
    });
    filtered.forEach((c) => {
      const arr = m.get(c.column_id);
      if (arr) arr.push(c);
    });
    m.forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return m;
  }, [cards, columns, selectedDay, undatedSelected]);

  /**
   * Board-wide search: whole permission-scoped board bundle (single board_id
   * query), never the date-filtered rendered array.
   */
  const searchResults = useMemo(() => {
    const q = debouncedSearch;
    if (!q) return [] as Array<{
      id: string;
      title: string;
      draft: boolean;
      card?: KCard;
      meta: string;
    }>;
    const colName = new Map(columns.map((c) => [c.id, c.name]));
    const hay = (parts: Array<string | null | undefined>) =>
      parts.filter(Boolean).join(" ").toLowerCase();
    const out: Array<{ id: string; title: string; draft: boolean; card?: KCard; meta: string }> = [];

    cards.forEach((c) => {
      const assignee = profileMap.get(c.assignee_id)?.full_name ?? "";
      const fvs = Object.values(fieldValuesByCard.get(c.id) ?? {});
      if (
        !hay([c.title, c.id, c.note, assignee, c.priority, colName.get(c.column_id), c.work_date, ...fvs]).includes(q)
      )
        return;
      out.push({
        id: c.id,
        title: c.title,
        draft: false,
        card: c,
        meta: [
          c.work_date ? format(parseISO(c.work_date), "d MMM") : "No date",
          assignee || "Unassigned",
          c.priority,
          colName.get(c.column_id) ?? "",
        ]
          .filter(Boolean)
          .join(" · "),
      });
    });

    if (isManager) {
      (bundle?.drafts ?? []).forEach((d) => {
        const fvs = Object.values(fieldValuesByCard.get(d.id) ?? {});
        if (!hay([d.title, d.id, d.note, d.priority, ...fvs]).includes(q)) return;
        out.push({
          id: d.id,
          title: d.title,
          draft: true,
          meta: `Unassigned · New Tasks · ${d.priority}`,
        });
      });
    }
    return out.slice(0, 30);
  }, [debouncedSearch, cards, columns, profileMap, fieldValuesByCard, bundle?.drafts, isManager]);

  const openSearchResult = (r: { draft: boolean; card?: KCard }) => {
    if (r.draft) {
      navigate({ to: "/boards/$id/timeline", params: { id } });
      return;
    }
    const c = r.card!;
    if (!prevView) setPrevView({ day: selectedDay, undated: undatedSelected });
    if (c.work_date) {
      const d = parseISO(c.work_date);
      setUndatedSelected(false);
      setWeekAnchor(d);
      setSelectedDay(d);
    } else {
      setSelectedDay(null);
      setUndatedSelected(true);
    }
    setDrawerCard(c);
    setSearch("");
    setDebouncedSearch("");
  };

  const clearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    if (prevView) {
      setSelectedDay(prevView.day);
      setUndatedSelected(prevView.undated);
      setWeekAnchor(prevView.day);
      setPrevView(null);
    }
  };

  const canManageFields = isManager;
  const canManageMembers = isManager && !isPersonal;
  const cardCountByUser = useMemo(() => {
    const m = new Map<string, number>();
    cards.forEach((c) => m.set(c.assignee_id, (m.get(c.assignee_id) ?? 0) + 1));
    return m;
  }, [cards]);

  const cardDraggable = (card: KCard) => {
    if (isManager) return true;
    return card.assignee_id === user?.id;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const onDragStart = (e: DragStartEvent) => {
    const c = cards.find((x) => x.id === e.active.id);
    if (c) setActiveCard(c);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveCard(null);
    const { active, over } = e;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const activeData = active.data.current as { type: string; card?: KCard } | undefined;
    const overData = over.data.current as { type: string; columnId?: string; card?: KCard } | undefined;
    if (!activeData || activeData.type !== "card" || !activeData.card) return;

    const original = activeData.card;
    let targetColId: string;
    let targetIndex: number;
    const colCardsCur = (colId: string) => (cardsByColumn.get(colId) ?? []);

    if (overData?.type === "column") {
      targetColId = overData.columnId!;
      targetIndex = colCardsCur(targetColId).length;
    } else if (overData?.type === "card" && overData.card) {
      targetColId = overData.card.column_id;
      const arr = colCardsCur(targetColId);
      const overIdx = arr.findIndex((c) => c.id === overId);
      targetIndex = overIdx === -1 ? arr.length : overIdx;
    } else {
      return;
    }

    if (original.column_id === targetColId) {
      const oldIdx = colCardsCur(original.column_id).findIndex((c) => c.id === activeId);
      const moved = arrayMove(
        colCardsCur(original.column_id),
        oldIdx,
        Math.min(targetIndex, colCardsCur(original.column_id).length - 1),
      );
      const before = moved[Math.max(0, moved.findIndex((c) => c.id === activeId) - 1)];
      const after = moved[moved.findIndex((c) => c.id === activeId) + 1];
      const newPos = midpoint(
        before && before.id !== activeId ? before.position : null,
        after && after.id !== activeId ? after.position : null,
      );
      const nextLocal = cards.map((c) => (c.id === activeId ? { ...c, position: newPos } : c));
      setLocalCards(nextLocal);
      const { error } = await sb
        .from("kanban_cards")
        .update({ position: newPos } as never)
        .eq("id", activeId);
      if (error) {
        setLocalCards(null);
        toast.error(error.message);
      } else {
        qc.invalidateQueries({ queryKey: ["kanban", "board", id] });
      }
      return;
    }

    let targetArr = colCardsCur(targetColId).filter((c) => c.id !== activeId);
    targetArr = [
      ...targetArr.slice(0, targetIndex),
      { ...original, column_id: targetColId },
      ...targetArr.slice(targetIndex),
    ];
    const idxInTarget = targetArr.findIndex((c) => c.id === activeId);
    const before = targetArr[idxInTarget - 1];
    const after = targetArr[idxInTarget + 1];
    const newPos = midpoint(before?.position ?? null, after?.position ?? null);

    const nextLocal = cards.map((c) =>
      c.id === activeId ? { ...c, column_id: targetColId, position: newPos } : c,
    );
    setLocalCards(nextLocal);
    const { error } = await sb
      .from("kanban_cards")
      .update({ column_id: targetColId, position: newPos } as never)
      .eq("id", activeId);
    if (error) {
      setLocalCards(null);
      toast.error(error.message);
    } else {
      qc.invalidateQueries({ queryKey: ["kanban", "board", id] });
    }
  };

  if (isLoading) {
    return (
      <div className="kb-page-bg px-[22px] py-6">
        <div className="h-64 animate-pulse rounded-2xl bg-white/50" />
      </div>
    );
  }
  if (!board) {
    return (
      <div className="kb-page-bg px-[22px] py-6">
        <div className="kb-glass p-6 text-[13px]">
          Board not found or you don't have access.
          <div className="mt-3">
            <Link to="/boards" className="text-[#7C3AED] underline">
              Back to boards
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (board.status === "pending_approval") {
    return (
      <div className="kb-page-bg px-[22px] py-6">
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 text-[13px] text-amber-900">
          This personal board is awaiting admin approval.
        </div>
      </div>
    );
  }

  const memberAvatars = members.slice(0, 6).map((m) => profileMap.get(m.user_id));
  const totalCards = cards.length;

  const finalColumnIdx = columns.length - 1;

  return (
    <div className="kb-page-bg">
      <div className="px-4 pt-4 sm:px-[22px]">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Link to="/boards" className="rounded-full p-1.5 hover:bg-white/70">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <h1 className="text-[22px] font-extrabold leading-tight tracking-tight text-slate-900">
                {board.name}
              </h1>
              <div className="text-[11.5px] text-slate-500">
                {isPersonal
                  ? "Personal"
                  : `Department · ${board.department}`}
                {isManager && !isPersonal && <> · {members.length} members</>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && clearSearch()}
                placeholder="Search this board…"
                className="h-9 w-[220px] rounded-full bg-white pl-8 pr-7 text-[12px] text-slate-700 shadow-sm ring-1 ring-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              {debouncedSearch && (
                <div className="absolute left-0 top-[calc(100%+6px)] z-30 max-h-[340px] w-[320px] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-xl">
                  {searchResults.length === 0 ? (
                    <div className="px-3 py-4 text-center text-[12px] text-slate-500">
                      No tasks found in this board
                    </div>
                  ) : (
                    searchResults.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => openSearchResult(r)}
                        className="block w-full rounded-xl px-3 py-2 text-left hover:bg-slate-50"
                      >
                        <div className="truncate text-[12.5px] font-semibold text-slate-800">
                          {r.title}
                        </div>
                        <div className="truncate text-[11px] text-slate-500">{r.meta}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {isManager && (
              <Link
                to="/boards/$id/timeline"
                params={{ id }}
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-3 text-[11.5px] font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
                title="Assign Work"
              >
                <CalendarRange className="h-3.5 w-3.5" /> Assign Work
              </Link>
            )}
            <button
              type="button"
              title="Columns"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-600 shadow-sm ring-1 ring-slate-200"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setSelectedDay(null)}
              title={selectedDay ? "Clear day filter" : "Filter"}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full shadow-sm ring-1",
                selectedDay
                  ? "bg-violet-100 text-violet-700 ring-violet-200"
                  : "bg-white text-slate-600 ring-slate-200",
              )}
            >
              <Filter className="h-3.5 w-3.5" />
            </button>
            {canManageMembers && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMembersOpen(true)}
                className="h-9 rounded-full"
              >
                <Users className="h-3.5 w-3.5" /> Members
              </Button>
            )}
            {canManageFields && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManageOpen(true)}
                className="h-9 rounded-full"
              >
                <Settings2 className="h-3.5 w-3.5" /> Fields
              </Button>
            )}
            {isManager && !isPersonal && (
              <div className="flex -space-x-2">
                {memberAvatars.map((a, i) => (
                  <UserAvatar
                    key={i}
                    name={a?.full_name}
                    avatarUrl={a?.avatar_url}
                    className={cn(
                      "h-7 w-7 ring-2 ring-white",
                      wipFlagged.has(members[i].user_id) && "ring-orange-400",
                    )}
                  />
                ))}
              </div>
            )}
            {isManager && (
              <button
                type="button"
                onClick={() => setNewCardOpen(true)}
                className="kb-btn-primary inline-flex h-9 items-center gap-1.5 rounded-full px-4 text-[12px] font-semibold"
              >
                <Plus className="h-3.5 w-3.5" /> New Task
              </button>
            )}
          </div>
        </div>

        <div className="mt-4">
          <WeeklyStrip
            cards={cards}
            selected={selectedDay}
            weekAnchor={weekAnchor}
            onSelect={(d) => {
              setSelectedDay(d);
              if (d) setUndatedSelected(false);
            }}
            showUndated={isPersonal}
            undatedSelected={undatedSelected}
            onToggleUndated={() => {
              setUndatedSelected((v) => !v);
              setSelectedDay(null);
            }}
          />
        </div>
      </div>

      {!isPersonal && wipFlagged.size > 0 && (
        <div className="mx-4 mt-3 rounded-2xl border border-orange-200 bg-orange-50/80 px-4 py-2 text-[11px] text-orange-900 backdrop-blur sm:mx-[22px]">
          {wipFlagged.size} member{wipFlagged.size === 1 ? "" : "s"} have 3+
          in-progress cards. Soft warning — consider rebalancing.
        </div>
      )}

      <div className="overflow-x-auto p-4 sm:p-[22px]">
        {totalCards === 0 ? (
          <div className="kb-glass mx-auto max-w-md p-10 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 text-violet-600">
              <Plus className="h-7 w-7" />
            </div>
            <h3 className="text-[15px] font-bold text-slate-900">
              {isManager ? "Create the first card" : "Nothing assigned yet"}
            </h3>
            <p className="mt-1 text-[12px] text-slate-500">
              {isManager
                ? "Add a card to get this board going."
                : "Cards assigned to you will appear here."}
            </p>
            {isManager && (
              <button
                type="button"
                onClick={() => setNewCardOpen(true)}
                className="kb-btn-primary mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold"
              >
                <Plus className="h-3.5 w-3.5" /> Create card
              </button>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            autoScroll={{ acceleration: 6 }}
          >
            <div className="flex w-full items-start gap-5 overflow-x-auto pb-2">
              {columns.map((col, idx) => (
                <KanbanColumn
                  key={col.id}
                  column={col}
                  cards={cardsByColumn.get(col.id) ?? []}
                  assigneeMap={profileMap}
                  commentCounts={new Map()}
                  wipFlagged={wipFlagged}
                  cardDraggable={cardDraggable}
                  fieldDefs={fieldDefs}
                  fieldValuesByCard={fieldValuesByCard}
                  canPush={isManager}
                  isFirst={idx === 0}
                  isFinal={idx === finalColumnIdx}
                  tintIndex={idx}
                  hideAssignee={isPersonal}
                  onAddCard={() => setNewCardOpen(true)}
                  onCardClick={(c) => setDrawerCard(c)}
                  cardActions={cardActions}
                  onEditCard={(c) => setEditCard(c)}
                  onDeleteCard={(c) => setDeleteCardObj(c)}
                />
              ))}
            </div>
            <DragOverlay
              dropAnimation={{
                duration: 250,
                easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
              }}
            >
              {activeCard ? (
                <KanbanCardFace
                  card={activeCard}
                  assigneeName={profileMap.get(activeCard.assignee_id)?.full_name}
                  assigneeAvatar={profileMap.get(activeCard.assignee_id)?.avatar_url}
                  fieldDefs={fieldDefs}
                  fieldValues={fieldValuesByCard.get(activeCard.id) ?? {}}
                  hideAssignee={isPersonal}
                  draggable
                  overlay
                  onClick={() => {}}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>


      {drawerCard && (() => {
        const live = cards.find((c) => c.id === drawerCard.id) ?? drawerCard;
        const acts = cardActions(live);
        return (
          <CardDrawer
            card={live}
            fieldDefs={fieldDefs}
            assigneeMap={profileMap}
            onClose={() => setDrawerCard(null)}
            isManager={isManager}
            isPersonal={isPersonal}
            canEdit={acts.canEdit}
            canDelete={acts.canDelete}
            onEditRequest={() => setEditCard(live)}
            onDeleteRequest={() => setDeleteCardObj(live)}
          />
        );
      })()}

      {editCard && (
        <EditCardDialog
          open={!!editCard}
          onOpenChange={(v) => !v && setEditCard(null)}
          card={editCard}
          fieldDefs={fieldDefs}
          memberProfiles={memberProfiles}
          isPersonal={isPersonal}
        />
      )}

      <AlertDialog
        open={!!deleteCardObj}
        onOpenChange={(v) => !v && setDeleteCardObj(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this card?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the card and its comments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCardObj && handleDeleteCard(deleteCardObj)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {newCardOpen && (
        <NewCardDialog
          open={newCardOpen}
          onOpenChange={setNewCardOpen}
          boardId={id}
          columns={columns}
          members={members}
          memberProfiles={memberProfiles}
          existingCards={cards}
          fieldDefs={fieldDefs}
          isPersonal={isPersonal}
          ownerId={user?.id}
        />
      )}

      {manageOpen && (
        <ManageFieldsDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          boardId={id}
          fields={fieldDefs}
        />
      )}

      {membersOpen && (
        <ManageMembersDialog
          open={membersOpen}
          onOpenChange={setMembersOpen}
          boardId={id}
          memberIds={members.map((m) => m.user_id)}
          allProfiles={activeProfilesList.map((p) => ({
            id: p.id as string,
            full_name: p.full_name as string,
            avatar_url: (p.avatar_url as string | null) ?? null,
            department: p.department as string,
          }))}
          hasCardCount={(uid) => cardCountByUser.get(uid) ?? 0}
        />
      )}
    </div>
  );
}
